/**
 * In-memory Supabase stand-in for tests and for the "demo team" mode.
 * Models exactly what the real backend does that the engine cares about:
 * server-owned updated_at, soft delete, RLS rejection, 401, network loss.
 */
import type { SyncTable } from '../types';
import { RemoteError, type RemoteApi, type RemoteRow } from './remote';

export class FakeRemote implements RemoteApi {
  tables = new Map<SyncTable, Map<string, RemoteRow>>();
  /** Simulated failure modes. */
  offline = false;
  unauthorized = false;
  /** Row ids the current user is not allowed to write (simulates RLS). */
  forbidden = new Set<string>();
  /** Monotonic server clock, ms. */
  clock = Date.UTC(2026, 8, 18, 12, 0, 0);
  calls: { op: string; table: SyncTable; n: number }[] = [];

  private tick(): string {
    this.clock += 1;
    return new Date(this.clock).toISOString();
  }
  private t(table: SyncTable): Map<string, RemoteRow> {
    let m = this.tables.get(table);
    if (!m) {
      m = new Map();
      this.tables.set(table, m);
    }
    return m;
  }
  private guard(): void {
    if (this.offline) throw new RemoteError('fetch failed', 'network');
    if (this.unauthorized) throw new RemoteError('JWT expired', 'auth', '401');
  }

  /** Server-side edit by "another device". */
  serverPut(table: SyncTable, row: Omit<RemoteRow, 'updated_at'> & { updated_at?: string }): RemoteRow {
    // A server-side write always gets a fresh server timestamp (the trigger).
    const r: RemoteRow = { ...row, deleted_at: row.deleted_at ?? null, updated_at: this.tick() } as RemoteRow;
    this.t(table).set(r.id, r);
    return r;
  }

  async upsert(table: SyncTable, rows: Record<string, unknown>[]): Promise<RemoteRow[]> {
    this.guard();
    this.calls.push({ op: 'upsert', table, n: rows.length });
    for (const r of rows) {
      if ('deleted_at' in r || 'updated_at' in r) {
        throw new Error('client must never send server-owned columns');
      }
      if (this.forbidden.has(String(r.id))) {
        throw new RemoteError('new row violates row-level security policy', 'rejected', '42501');
      }
    }
    const out: RemoteRow[] = [];
    for (const r of rows) {
      const prev = this.t(table).get(String(r.id));
      const next: RemoteRow = {
        ...(prev ?? {}),
        ...r,
        id: String(r.id),
        team_id: String(r.team_id),
        deleted_at: prev?.deleted_at ?? null, // upsert never touches deleted_at
        updated_at: this.tick(),
      };
      this.t(table).set(next.id, next);
      out.push(next);
    }
    return out;
  }

  async softDelete(table: SyncTable, ids: string[]): Promise<void> {
    this.guard();
    this.calls.push({ op: 'delete', table, n: ids.length });
    for (const id of ids) {
      const r = this.t(table).get(id);
      if (r) this.t(table).set(id, { ...r, deleted_at: this.tick(), updated_at: this.tick() });
    }
  }

  async restore(table: SyncTable, ids: string[]): Promise<void> {
    this.guard();
    this.calls.push({ op: 'restore', table, n: ids.length });
    for (const id of ids) {
      const r = this.t(table).get(id);
      if (r) this.t(table).set(id, { ...r, deleted_at: null, updated_at: this.tick() });
    }
  }

  async pull(table: SyncTable, teamId: string, since: string | null): Promise<{ rows: RemoteRow[]; serverNow: string }> {
    this.guard();
    this.calls.push({ op: 'pull', table, n: 0 });
    const rows = [...this.t(table).values()]
      .filter((r) => r.team_id === teamId && (!since || r.updated_at > since))
      .sort((a, b) => (a.updated_at < b.updated_at ? -1 : a.updated_at > b.updated_at ? 1 : a.id < b.id ? -1 : 1))
      .slice(0, 1000);
    return { rows, serverNow: new Date(this.clock).toISOString() };
  }
}
